import * as React from "react";
import {useState} from "react";
import {Card} from "../common/Card";
import {AonColors} from "../../../../css/Colors";
import {INFORM_RATINGS} from "../../utils/ratingFormatUtil";
import {DonutChart} from "../aon-united-charts/DonutChart";
import {IInformSubComponentSummary} from "../../model/inform/Inform";
import {IInformSubComponentDetails} from "../../model/inform/InformSubComponentDetails";
import {IInformSubComponentFactor} from "../../model/inform/InformSubComponentDetails";

export interface IInformSubComponentProps {
    name: string;
    hasData: boolean;
    summary: IInformSubComponentSummary | undefined;
    details?: IInformSubComponentDetails | undefined;
}

export const InformSubComponent: React.FunctionComponent<IInformSubComponentProps> = (props) => {
    const [expanded, setExpanded] = useState<boolean>(false);
    const informRating = INFORM_RATINGS.find(x => x.rating === props.summary?.rating.toLowerCase());

    function handleExpandChange() {
        setExpanded(!expanded);
    }

    const renderDetailsTable = () => {
        return <table className="inform-sub-component__details-table">
            <thead className="inform-sub-component__details-table-header">
            <tr>
                <th>Factor</th>
                <th>Aon Rating</th>
                <th>Change</th>
                <th>Value</th>
                {props.details?.hasThreshold && <th>Threshold</th>}
                <th>Missing Data</th>
                <th/>
            </tr>
            </thead>
            {renderDetailsTableBody()}
        </table>;
    };

    const renderDetailsTableBody = () => {
        return <tbody>
        {
            props.details?.data?.map((details: IInformSubComponentFactor) => {
                return (<tr key={details.factor} className="inform-sub-component__details-table-row">
                    <td>{details.factor}</td>
                    <td>{details.rating}</td>
                    <td>{details.change}</td>
                    <td>{renderValues(details)}</td>
                    {props.details?.hasThreshold && <td>{details.threshold}</td>}
                    <td/>
                    <td>+</td>
                </tr>);
            })
        }
        </tbody>;
    };

    const renderValues = (details: IInformSubComponentFactor) => {
        return details.value.map((it) => <ul key={it}>{it}</ul>);
    };

    const renderDetails = () => {
        return <div className="inform-sub-component__details" data-testid="inform-sub-component__details">
            <div className="inform-sub-component__details__header" data-testid="inform-sub-component__details__header">
                <div className="inform-sub-component__details__header-name">Details</div>
            </div>
            <div className="inform-sub-component__details__body">
                {renderDetailsTable()}
            </div>
        </div>;
    };

    const renderSummary = () => {
        return <div className="inform-sub-component__summary" data-testid="inform-sub-component__summary">
            <div className="inform-sub-component__summary-header"
                 data-testid="inform-sub-component__summary-header">
                <div className="inform-sub-component__summary-header-name">Summary</div>
                <div className="inform-sub-component__summary-header-rating-change">
                    {props.summary?.ratingChange}
                </div>
            </div>
            <div className="inform-sub-component__summary-body">
                <div className="inform-sub-component__summary-body-rating"
                     data-testid="inform-sub-component__summary-body-rating">
                    <div className="inform-sub-component__summary-body-rating-text">{props.name} Inform Rating</div>
                    <div className={`inform-sub-component__rating-icon rating-icon ${informRating?.ratingClass}`}
                         data-testid="inform-sub-component__summary-body-rating-svg"/>
                    <div className="inform-sub-component__current-rating"
                         data-testid="inform-sub-component__summary-body-rating-value">{props.summary!.rating}</div>
                </div>
                <div className="inform-sub-component__summary-body-factors"
                     data-testid="inform-sub-component__summary-body-factors">
                    <div className="inform-sub-component__summary-body-factors-up">
                        <i className="fa fa-regular fa-arrow-up icon up"
                           data-testid="inform-sub-component__summary-body-factors-arrow-up"/>
                        <div className="inform-sub-component__summary-body-factors-up-text">
                            {props.summary?.upgraded} Upgraded Factors
                        </div>
                    </div>
                    <div className="inform-sub-component__summary-body-factors-down">
                        <i className="fa fa-regular fa-arrow-down icon down"
                           data-testid="inform-sub-component__summary-body-factors-arrow-down"/>
                        <div className="inform-sub-component__summary-body-factors-down-text">
                            {props.summary?.downgraded} Downgraded Factors
                        </div>
                    </div>
                </div>
                <div className="inform-sub-component__summary-body-data-completion"
                     data-testid="inform-sub-component__summary-body-data-completion">
                    <DonutChart completion={props.summary?.dataCompletion}/>
                </div>
            </div>
        </div>;
    };

    const renderCardContent = () => {
        return <div className="inform-sub-component__card-expand-container"
                    data-testid="inform-sub-component__card-expand-container">
            {props.summary && renderSummary()}
            {props.details && renderDetails()}
            <div className="json-container">
                {JSON.stringify(props.summary, null, " ")}
            </div>
        </div>;
    };


    return <div className="inform-sub-component__container paper-style" data-testid="inform-sub-component__container">
                <div className="inform-sub-component__rating-top">
                    <h3 className="inform-sub-component__rating-title">{props.name}</h3>
                    {props.summary ? <div className="inform-sub-component__rating-container">
                        <span
                            className={`inform-sub-component__rating-icon rating-icon ${informRating?.ratingClass}`}
                            role={`${informRating?.ratingClass}`}/>
                        <span className="inform-sub-component__current-rating">{props.summary?.rating}</span>
                    </div>
                    : null
                    }
                </div>
                {props.hasData
                ? <Card
                    className={`inform-sub-component-cards ${expanded ? "open" : "close"}`}
                    expanded={expanded} onExpandChange={() => handleExpandChange()}
                    header={<div>{informRating?.ratingText}</div>}
                    showExpandableButton={true}
                    childrenContainerStyle={{backgroundColor: AonColors.ActualWhite}}>
                    {expanded ? renderCardContent() : null}</Card>
                : null}
            </div>;
};